const DEFAULT_TIMEZONE = 'America/Toronto'
const LOCALE = 'en-CA'

export interface FormatDateOptions {
	timeZone?: string
}

const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/

/**
 * Parse a date string safely. Date-only strings like "2026-04-30" are parsed
 * as noon UTC to prevent the day from shifting when rendered in a western
 * hemisphere timezone (e.g. America/Toronto is UTC-4/5, so midnight UTC
 * would show as the previous day).
 */
function parseDate(value: string | Date): Date {
	if (value instanceof Date) return value
	if (DATE_ONLY_RE.test(value)) {
		return new Date(`${value}T12:00:00Z`)
	}
	return new Date(value)
}

export function formatDate(
	date: string | Date | null | undefined,
	{ timeZone = DEFAULT_TIMEZONE }: FormatDateOptions = {},
): string {
	if (!date) return '-'
	const d = parseDate(date)
	if (Number.isNaN(d.getTime())) return String(date)
	return d.toLocaleDateString(LOCALE, {
		year: 'numeric',
		month: 'long',
		day: 'numeric',
		timeZone,
	})
}

export function formatShortDate(
	date: string | Date | null | undefined,
	{ timeZone = DEFAULT_TIMEZONE }: FormatDateOptions = {},
): string {
	if (!date) return '-'
	const d = parseDate(date)
	if (Number.isNaN(d.getTime())) return String(date)
	return d.toLocaleDateString(LOCALE, {
		year: 'numeric',
		month: 'short',
		day: 'numeric',
		timeZone,
	})
}

export function formatDateTime(
	date: string | Date | null | undefined,
	{ timeZone = DEFAULT_TIMEZONE }: FormatDateOptions = {},
): string {
	if (!date) return '-'
	const d = parseDate(date)
	if (Number.isNaN(d.getTime())) return String(date)
	return d.toLocaleString(LOCALE, {
		year: 'numeric',
		month: 'long',
		day: 'numeric',
		hour: 'numeric',
		minute: '2-digit',
		timeZone,
	})
}

/**
 * Landlord Pipeline card timestamps ("Started On" / "Requested On" /
 * "Invited On") — day-month-year order with a zero-padded 12h time
 * (client spec 2026-08: "22 July, 2026, 06:00 PM"), distinct from
 * formatDateTime's month-day-year/"at h:mm a.m." style used elsewhere.
 */
export function formatPipelineDateTime(
	date: string | Date | null | undefined,
	{ timeZone = DEFAULT_TIMEZONE }: FormatDateOptions = {},
): string {
	if (!date) return '-'
	const d = parseDate(date)
	if (Number.isNaN(d.getTime())) return String(date)
	const parts = new Intl.DateTimeFormat('en-US', {
		day: 'numeric',
		month: 'long',
		year: 'numeric',
		timeZone,
	}).formatToParts(d)
	const day = parts.find((p) => p.type === 'day')?.value ?? ''
	const month = parts.find((p) => p.type === 'month')?.value ?? ''
	const year = parts.find((p) => p.type === 'year')?.value ?? ''
	const time = new Intl.DateTimeFormat('en-US', {
		hour: '2-digit',
		minute: '2-digit',
		hour12: true,
		timeZone,
	}).format(d)
	return `${day} ${month}, ${year}, ${time.toUpperCase()}`
}

export function formatDOB(
	date: string | Date | null | undefined,
	{ timeZone = DEFAULT_TIMEZONE }: FormatDateOptions = {},
): string {
	if (!date) return '-'
	const d = parseDate(date)
	if (Number.isNaN(d.getTime())) return String(date)
	return d.toLocaleDateString(LOCALE, {
		year: 'numeric',
		month: 'long',
		day: 'numeric',
		timeZone,
	})
}

export function formatMoveInDate(
	date?: string | null,
	{ timeZone = DEFAULT_TIMEZONE }: FormatDateOptions = {},
): string {
	if (!date || date === 'Flexible') return 'Flexible'
	try {
		const d = parseDate(date)
		if (Number.isNaN(d.getTime())) return 'Flexible'
		return d.toLocaleDateString(LOCALE, {
			day: 'numeric',
			month: 'long',
			year: 'numeric',
			timeZone,
		})
	} catch {
		return 'Flexible'
	}
}
